import { useMsal } from "@azure/msal-react";
import {
  Column,
  DefaultCellTypes,
  NumberCell,
  Row,
  TextCell,
} from "@silevis/reactgrid";
import axios, { CancelTokenSource } from "axios";
import { useCallback, useEffect, useRef } from "react";
import { useData } from "../Components/Contexts/DataProvider";
import { useEntities } from "../Components/Contexts/EntitiesProvider";
import { useHistoryPeriod } from "../Components/Contexts/HistoryPeriodProvider";
import { useLoggedInUser } from "../Components/Contexts/LoggedInUserProvider";
import { useSelectedEntity } from "../Components/Contexts/SelectedEntityProvider";
import { useSelectedTab } from "../Components/Contexts/SelectedTabProvider";
import {
  buildSummaryRowsByCategory,
  getDeltaRows,
  normalize,
} from "../Components/Table/SummaryTable";
import {
  budgetPriorityColumns,
  ftePriorityColumns,
  historyBudgetPriorityColumns,
  historyFtePriorityColumns,
  numericFields,
  originalFtePriorityColumns,
  slovakMonthHeaders,
  sumFields,
} from "../Components/Table/TableHelpers";
import {
  renameField,
  selectedTabSwitch,
  sortColumns,
} from "../General/Helpers";
import useDataverseService from "./DataverseService";

const mergeUniqueEntities = (arr1: any, arr2: any) => {
  const combined = [...arr1, ...arr2];
  const uniqueEntities = new Map();
  combined.forEach((item) => {
    uniqueEntities.set(item.new_entityownerid, item);
  });
  return Array.from(uniqueEntities.values());
};

export const useLoadData = () => {
  const dataverseService = useDataverseService();
  const { instance } = useMsal();
  const { setLoggedInUserId } = useLoggedInUser();
  const { selectedTab } = useSelectedTab();
  const { selectedEntity, setSelectedEntity } = useSelectedEntity();
  const { setEntities } = useEntities();
  const { setData, setOriginalData } = useData();

  const reqSourceRef = useRef<CancelTokenSource | null>(null);

  const loadData = useCallback(async () => {
    if (reqSourceRef.current) {
      reqSourceRef.current.cancel("loadData superseded");
    }
    const source = axios.CancelToken.source();
    reqSourceRef.current = source;

    try {
      const users = await dataverseService.getUsers();
      const currentUser = users.find(
        (u: any) => u.domainname === instance.getActiveAccount()?.username
      );
      setLoggedInUserId(currentUser.systemuserid);

      const owners: any = await dataverseService.fetchData(
        "new_entityowners",
        source.token
      );
      if (!owners?.data?.value) return;

      const currentOwners = owners.data.value.filter(
        (o: any) => o._ownerid_value === currentUser.systemuserid
      );

      const distinctValues = await dataverseService.fetchDistinctValues(
        "cr864_budgetrecords",
        "cr864_codecompany",
        source.token
      );
      const distinctFtes = await dataverseService.fetchDistinctValues(
        "new_headcounts",
        "new_codecompany",
        source.token
      );

      const combinedDistinct = new Set([
        ...distinctValues.value.map((d: any) => d.cr864_codecompany),
        ...distinctFtes.value.map((d: any) => d.new_codecompany),
      ]);
      const disctinctOwners = owners.data.value.filter((o: any) =>
        combinedDistinct.has(o.new_codecompany)
      );

      const mergedEntities = mergeUniqueEntities(
        currentOwners,
        disctinctOwners
      );
      setEntities(mergedEntities);
      if (!selectedEntity) setSelectedEntity(mergedEntities[0]);

      const selectedCompanyCode =
        selectedEntity?.new_codecompany || mergedEntities[0]?.new_codecompany;

      const url = selectedTabSwitch(selectedTab);

      let allRecords: any[] = [];
      let nextLink: string | undefined;

      const pageData: any = await dataverseService.fetchData(
        url ?? "",
        source.token,
        selectedCompanyCode
      );
      if (!pageData?.data) {
        setData({ value: [], headCount: [] });
        setOriginalData({ value: [], headCount: [] });
        return;
      }

      allRecords = [...pageData.data.value];
      nextLink = pageData.nextLink;

      while (nextLink) {
        const tempPage = await dataverseService.fetchNextPage(
          nextLink,
          source.token
        );
        if (!tempPage?.data?.value) break;
        allRecords.push(...tempPage.data.value);
        nextLink = tempPage.nextLink;
      }

      let allHeadCounts: any[] = [];
      const headCountData: any = await dataverseService.fetchData(
        "new_headcounts",
        source.token,
        selectedCompanyCode
      );
      if (headCountData?.data?.value) {
        allHeadCounts = [...headCountData.data.value];
        let headNextLink = headCountData.nextLink;
        while (headNextLink) {
          const tempPage = await dataverseService.fetchNextPage(
            headNextLink,
            source.token
          );
          if (!tempPage?.data?.value) break;
          allHeadCounts.push(...tempPage.data.value);
          headNextLink = tempPage.nextLink;
        }
      }

      setOriginalData({
        value: JSON.parse(JSON.stringify(allRecords)),
        headCount: JSON.parse(JSON.stringify(allHeadCounts)),
      });
      setData({
        value: JSON.parse(JSON.stringify(allRecords)),
        headCount: JSON.parse(JSON.stringify(allHeadCounts)),
      });
    } catch (err: any) {
      if (axios.isCancel(err)) return;

      console.error("loadData failed:", err);
      setData({ value: [], headCount: [] });
      setOriginalData({ value: [], headCount: [] });
    } finally {
      if (reqSourceRef.current === source) {
        reqSourceRef.current = null;
      }
    }
  }, [
    dataverseService,
    instance,
    selectedTab,
    selectedEntity,
    setLoggedInUserId,
    setEntities,
    setSelectedEntity,
    setData,
    setOriginalData,
  ]);

  useEffect(() => {
    return () => reqSourceRef.current?.cancel("unmount");
  }, []);

  return { loadData };
};

export const useCsvExport = () => {
  const { data } = useData();
  const { selectedTab } = useSelectedTab();
  const { historyPeriod } = useHistoryPeriod();
  const { getCostCenters } = useDataverseService();
  const { selectedEntity } = useSelectedEntity();
  const { fetchData, fetchNextPage } = useDataverseService();

  const handleCsvExport = async () => {
    const columns: Column[] = Object.keys(data.value[0]).map((c) => ({
      columnId: c,
    }));

    const costCenters = await getCostCenters();

    let priorityColumns: string[] = [];
    let excludeFields: string[] = [
      "@odata.etag",
      "_ownerid_value",
      "new_submitted",
    ];
    let isBudget = false;

    switch (selectedTab) {
      case "Budget":
        priorityColumns = budgetPriorityColumns.filter((col) => col != " ");
        excludeFields = [...excludeFields, "cr864_budgetrecordid"];
        isBudget = true;
        break;
      case "Original":
        priorityColumns = budgetPriorityColumns.filter((col) => col != " ");
        excludeFields = [...excludeFields, "cr864_budgetrecordcopyid"];
        isBudget = true;
        break;
      case "FTE":
        priorityColumns = ftePriorityColumns;
        excludeFields = [...excludeFields, "new_headcountid"];
        break;
      case "Original FTE":
        priorityColumns = originalFtePriorityColumns;
        excludeFields = [...excludeFields, "new_originalfteid"];
        break;
      case "History Budget":
        priorityColumns = historyBudgetPriorityColumns;
        excludeFields = [...excludeFields, "new_historybudgetid"];
        break;
      case "History FTE":
        priorityColumns = historyFtePriorityColumns;
        excludeFields = [...excludeFields, "new_historyfteid"];
        break;
    }

    const filteredColumns = columns.filter(
      (col) => !excludeFields.includes(String(col.columnId))
    );

    const sortedColumns = sortColumns(
      filteredColumns,
      priorityColumns,
      isBudget
    );
    const sortedColumnIds = sortedColumns.map((col) => col.columnId);

    const headers = sortedColumnIds
      .map((colId) => renameField(String(colId)))
      .join(",");

    const historyFilteredData = historyPeriod
      ? data.value.filter((row: any) => row.new_period === historyPeriod)
      : data.value;

    const rows = historyFilteredData.map((row: any) =>
      sortedColumnIds
        .map((colId) => {
          let value = row[colId];

          if (colId == "_new_costcenter_value") {
            let matchingCenter = costCenters.find(
              (center) => center.new_costcentersid === value
            );
            value = matchingCenter?.new_name ?? value;
          }

          if (value === null || value === undefined) value = "";

          if (typeof value === "string") {
            value = `"${value}"`;
          }

          return value;
        })
        .join(",")
    );

    const csv = [headers, ...rows].join("\n");

    const csvName = selectedTab + (historyPeriod ?? "");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = csvName;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const csvEscape = (raw: unknown) => {
    if (raw === null || raw === undefined) return "";
    const s = String(raw);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };

  const handleCellValue = (cell: DefaultCellTypes) => {
    if (!cell) return "";
    if ("value" in cell) return (cell as NumberCell).value ?? "";
    if ("text" in cell) return (cell as TextCell).text ?? "";
    return "";
  };

  const rowsToCsvLines = (rows: Row<DefaultCellTypes>[]) => {
    if (!rows || rows.length === 0) return [] as string[];

    const headerRow = rows.find((r) => String(r.rowId) === "header") ?? rows[0];

    const headers = headerRow.cells
      .map((cell: any) => {
        const text = "text" in cell ? cell.text : undefined;
        const header = text && text.length > 0 ? text : cell.groupId ?? "";
        return csvEscape(header);
      })
      .join(",");

    const body = rows
      .filter((r) => r !== headerRow)
      .map((r) => r.cells.map((c) => csvEscape(handleCellValue(c))).join(","));

    return [headers, ...body];
  };

  const downloadCsv = (csv: string, suggestedName: string) => {
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = suggestedName.endsWith(".csv")
      ? suggestedName
      : `${suggestedName}.csv`;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const summaryConfig: Record<
    string,
    {
      originalTableName: string;
      originalCategoryField: string;
      actualCategoryField: string;
    }
  > = {
    "Sumár - Zjednotená kategória": {
      originalTableName: "cr864_budgetrecordcopies",
      originalCategoryField: "cr864_unifiedcategory",
      actualCategoryField: "cr864_unifiedcategory",
    },
    "Sumár - Zložky mzdy": {
      originalTableName: "cr864_budgetrecordcopies",
      originalCategoryField: "cr864_collectioncategoryinbp",
      actualCategoryField: "cr864_collectioncategoryinbp",
    },
    "Sumár - ASIS/Legislatíva/Projekt": {
      originalTableName: "cr864_budgetrecordcopies",
      originalCategoryField: "cr864_asisproject",
      actualCategoryField: "cr864_asisproject",
    },
    "Summary FTE": {
      originalTableName: "new_originalftes",
      originalCategoryField: "new_zjednotenakategoria",
      actualCategoryField: "new_unifiedcategory",
    },
  };

  const handleSummaryCsvExport = async () => {
    const cfg = summaryConfig[selectedTab];
    if (!cfg) {
      downloadCsv("Unsupported summary tab", `${selectedTab || "Summary"}.csv`);
      return;
    }

    const monthFields =
      selectedTab === "Summary FTE" ? numericFields : sumFields;
    const monthHeaders = slovakMonthHeaders;
    const summaryColumn = selectedTab !== "Summary FTE";
    const unifiedSummary = selectedTab === "Sumár - Zjednotená kategória";

    let originalRows: any[] = [];
    if (selectedEntity?.new_codecompany) {
      let nextLink: string | undefined;
      const initial = await fetchData(
        cfg.originalTableName,
        undefined,
        selectedEntity.new_codecompany
      );
      if (initial?.data?.value) {
        originalRows = [...initial.data.value];
        nextLink = initial.nextLink;
        while (nextLink) {
          const nextPage = await fetchNextPage(nextLink);
          if (!nextPage?.data?.value) break;
          originalRows.push(...nextPage.data.value);
          nextLink = nextPage.nextLink;
        }
      }
    }

    const currentRows =
      selectedTab === "Summary FTE" ? data.headCount ?? [] : data.value ?? [];

    const allRows = [...(currentRows ?? []), ...(originalRows ?? [])];
    const tempMap = new Map<string, string>();
    allRows.forEach((r) => {
      const raw =
        r[cfg.actualCategoryField] ?? r[cfg.originalCategoryField] ?? "Iné";
      const norm = normalize(raw);
      if (!tempMap.has(norm)) tempMap.set(norm, raw);
    });

    let categoryMap: Map<string, string>;
    if (unifiedSummary) {
      const desiredOrder = ["lekár", "sestra", "OZP", "THP", "R", "Iné"];
      const normalizedOrder = desiredOrder.map(normalize);

      const sortedEntries = Array.from(tempMap.entries()).sort(([a], [b]) => {
        const ai = normalizedOrder.indexOf(a);
        const bi = normalizedOrder.indexOf(b);
        if (ai === -1 && bi === -1) return 0;
        if (ai === -1) return 1;
        if (bi === -1) return -1;
        return ai - bi;
      });

      categoryMap = new Map<string, string>(sortedEntries);
    } else {
      categoryMap = tempMap;
    }

    const normalizedKeys = Array.from(categoryMap.keys());
    const categories = normalizedKeys.map((norm) => categoryMap.get(norm)!);

    const originalTable = buildSummaryRowsByCategory({
      data: originalRows,
      categories,
      monthFields,
      monthHeaders,
      categoryField: cfg.originalCategoryField,
      groupFn: (catRaw) => normalize(catRaw),
      normToLabel: (norm) => categoryMap.get(norm) || norm,
      summaryColumn,
      unifiedSummary,
    });

    const actualTable = buildSummaryRowsByCategory({
      data: currentRows,
      categories,
      monthFields,
      monthHeaders,
      categoryField: cfg.actualCategoryField,
      groupFn: (catRaw) => normalize(catRaw),
      normToLabel: (norm) => categoryMap.get(norm) || norm,
      summaryColumn,
      unifiedSummary,
    });

    const deltaTable = getDeltaRows(
      originalRows,
      currentRows,
      cfg.originalCategoryField,
      cfg.actualCategoryField,
      categoryMap,
      monthFields,
      monthHeaders,
      (catRaw) => normalize(catRaw),
      (norm) => categoryMap.get(norm) || norm,
      summaryColumn,
      unifiedSummary
    );

    const blocks: string[] = [];
    const pushBlock = (title: string, rows: Row<DefaultCellTypes>[]) => {
      if (!rows?.length) return;
      blocks.push(csvEscape(`${title}`));
      blocks.push(...rowsToCsvLines(rows));
      blocks.push("");
    };

    pushBlock("Original", originalTable.rows);
    pushBlock("Actual", actualTable.rows);
    pushBlock("Delta", deltaTable.rows);

    const csv = blocks.join("\n");
    const fileName = selectedTab || "Summary";
    downloadCsv(csv, fileName);
  };

  return { handleCsvExport, handleSummaryCsvExport };
};
